import { grey, blue } from "@mui/material/colors"
import { createTheme } from "@mui/material/styles"

import { RESERVATION_COLORS } from "@/constants/reservations"

declare module "@mui/material/styles" {
  interface Palette {
    reservationPending: Palette["primary"]
    reservationConfirmed: Palette["primary"]
    reservationSeated: Palette["primary"]
    reservationNoShow: Palette["primary"]
    reservationCancelled: Palette["primary"]
    reservationRemoved: Palette["primary"]
    reservationWalkIn: Palette["primary"]
  }
  interface PaletteOptions {
    reservationPending?: PaletteOptions["primary"]
    reservationConfirmed?: PaletteOptions["primary"]
    reservationSeated?: PaletteOptions["primary"]
    reservationNoShow?: PaletteOptions["primary"]
    reservationCancelled?: PaletteOptions["primary"]
    reservationRemoved?: PaletteOptions["primary"]
    reservationWalkIn?: PaletteOptions["primary"]
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    "ui-element": true
  }
  interface ButtonPropsColorOverrides {
    grey: true
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsSizeOverrides {
    "x-small": true
  }
}

declare module "@mui/material/FormControl" {
  interface FormControlPropsSizeOverrides {
    "x-small": true
  }
}

let theme = createTheme({
  palette: {
    reservationPending: RESERVATION_COLORS.pending,
    reservationConfirmed: RESERVATION_COLORS.confirmed,
    reservationSeated: RESERVATION_COLORS.seated,
    reservationNoShow: RESERVATION_COLORS.no_show,
    reservationCancelled: RESERVATION_COLORS.cancelled,
    reservationRemoved: RESERVATION_COLORS.removed,
    reservationWalkIn: RESERVATION_COLORS.walk_in,
  },
  typography: {
    button: {
      textTransform: "none",
    },
  },
})

theme = createTheme(theme, {
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: "ui-element" },
          style: {
            border: `1px solid rgba(0, 0, 0, 0.23)`,
            background: "#fff",
          },
        },
        {
          props: { variant: "ui-element", color: "secondary" },
          style: {
            border: `1px solid ${blue[300]}`,
            background: blue[100],
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }: { ownerState: { variant?: string } }) => ({
          textTransform: "none",
          fontWeight: 500,
          lineHeight: "normal",
          borderRadius: 5,
          minWidth: 0,
          ...(ownerState.variant !== "ui-element" && {
            height: 32,
            fontSize: "14px",
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 16,
            paddingRight: 16,
          }),
        }),
        sizeSmall: ({ ownerState }: { ownerState: { variant?: string } }) =>
          ownerState.variant !== "ui-element"
            ? {
                height: 28,
                fontSize: "13px",
                paddingLeft: 12,
                paddingRight: 12,
              }
            : {},
        sizeLarge: ({ ownerState }: { ownerState: { variant?: string } }) =>
          ownerState.variant !== "ui-element"
            ? {
                height: 38,
                fontSize: "14px",
                paddingLeft: 20,
                paddingRight: 20,
              }
            : {},
        containedPrimary: {
          "&:hover": { backgroundColor: "#2696fa" },
          "&:active": { color: "#f3f3f3", backgroundColor: "#1f8ae8" },
          "&.Mui-focusVisible": { backgroundColor: "#2696fa" },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        grouped: {
          minWidth: "0px",
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          position: "static",
          transform: "none !important",
          marginBottom: "4px",
          fontSize: "0.75rem",
          fontWeight: 500,
          color: "#6b7280",
          "&.Mui-focused": {
            color: "#6b7280",
          },
          "&.Mui-error": {
            color: "#ef4444",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          position: "static",
          transform: "none !important",
          marginBottom: "4px",
          fontSize: "0.75rem",
          fontWeight: 500,
          color: "#6b7280",
          "&.Mui-focused": {
            color: "#6b7280",
          },
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          fontSize: "0.875rem",
          "&:hover": {
            backgroundColor: "#ffffff",
            borderColor: "#d1d5db",
          },
          "&.Mui-focused": {
            backgroundColor: "#ffffff",
            borderColor: "#9ca3af",
          },
          "&.Mui-disabled": {
            backgroundColor: "#f9fafb",
          },
          "&.Mui-error": {
            borderColor: "#ef4444",
          },
        },
        input: {
          padding: "8px 12px",
          paddingTop: "8px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          borderRadius: "6px",
          fontSize: "0.875rem",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e5e7eb",
            top: 0,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d1d5db",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#9ca3af",
            borderWidth: "1px",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ef4444",
          },
          "&.Mui-disabled": {
            backgroundColor: "#f9fafb",
          },
          "&.MuiInputBase-sizeX-small": {
            fontSize: "0.75rem",
            "& .MuiOutlinedInput-input": {
              paddingTop: "6px",
              paddingBottom: "6px",
              paddingLeft: "10px",
              paddingRight: "10px",
            },
            "&.MuiInputBase-adornedStart .MuiOutlinedInput-input": {
              paddingLeft: "0px",
            },
            "&.MuiInputBase-adornedEnd .MuiOutlinedInput-input": {
              paddingRight: "0px",
            },
          },
        },
        input: {
          padding: "8px 12px",
        },
        notchedOutline: {
          "& legend": {
            display: "none",
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
        slotProps: {
          inputLabel: {
            shrink: true,
          },
        },
      },
      styleOverrides: {
        root: {
          "& .MuiInputBase-root.MuiInputBase-sizeX-small": {
            fontSize: "0.75rem",
          },
          "& .MuiInputLabel-root.MuiInputLabel-sizeX-small": {
            fontSize: "0.75rem",
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {},
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        select: {
          padding: "8px 12px",
          fontSize: "0.875rem",
          ".MuiInputBase-sizeX-small &.MuiSelect-outlined": {
            paddingTop: "6px !important",
            paddingBottom: "6px !important",
            paddingLeft: "10px",
            paddingRight: "32px",
          },
          ".MuiInputBase-sizeX-small.MuiInputBase-adornedStart &.MuiSelect-outlined": {
            paddingLeft: "0px",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-sizeX-small .MuiSvgIcon-root": {
            fontSize: "1.1rem",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "12px",
        },
      },
    },
  },
  palette: {
    background: {
      default: "#f6f6f6",
    },
    secondary: {
      main: "#000000",
    },
    grey: theme.palette.augmentColor({
      color: {
        main: grey[500],
      },
      name: "grey",
    }),
  },
})

export default theme
